import type { Event, Quizrun } from "@/types/domain";
import { isVirtualLocationId } from "@/lib/virtual";

export interface RegistrationEvent {
  id: string;
  /** Underlying event row id, used for deep-linking to a registration dialog. */
  eventId: number;
  quizrunId: number;
  quizrunNumber: number;
  quizrunTitle?: string | null;
  quizrunKind?: string;
  quizrunLanguage: string;
  quizrunDateStart: string;
  quizrunDateFinish: string;
  name: string;
  date_start: string;
  date_end?: string;
  location: string;
  locationName?: string;
  locationLink?: string;
  routeLink?: string;
  isVirtual: boolean;
  /** Location country display name (e.g. "United Kingdom"); null for virtual. */
  country?: string | null;
  /** Per-location ticket price in minor units; null/0 = free. */
  priceCents?: number | null;
  /** Lowercase ISO currency for the price line. */
  priceCurrency?: string | null;
  /** IANA timezone of the event's location (undefined for virtual). */
  timezone?: string;
}

/** Formats a date as "6 February 2026" */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Formats a date range compactly:
 * - Same day: "8 February 2026 12:00"
 * - Same month: "8 – 28 February 2026"
 * - Same year: "15 March – 1 April 2026"
 * - Different years: "15 December 2025 – 1 January 2026"
 */
export const formatDateRange = (start: string, end?: string): string => {
  const s = new Date(start);
  if (!end) return formatDate(start);

  const e = new Date(end);
  // Date parts in UTC so the canonical edition window matches the pill
  // (RegistrationCards) and doesn't shift a day for negative-offset clients.
  const opts = { timeZone: "UTC" } as const;
  const sDay = s.toLocaleDateString("en-GB", { day: "numeric", ...opts });
  const sMonth = s.toLocaleDateString("en-GB", { month: "long", ...opts });
  const sYear = s.getUTCFullYear();
  const eDay = e.toLocaleDateString("en-GB", { day: "numeric", ...opts });
  const eMonth = e.toLocaleDateString("en-GB", { month: "long", ...opts });
  const eYear = e.getUTCFullYear();

  // Same day → show time (kept viewer-local: a timed event's clock is local).
  if (sameUtcDay(s, e)) {
    const time = s.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    return `${formatDate(start)} ${time}`;
  }
  // Same month & year
  if (sMonth === eMonth && sYear === eYear) {
    return `${sDay} – ${eDay} ${sMonth} ${sYear}`;
  }
  // Same year
  if (sYear === eYear) {
    return `${sDay} ${sMonth} – ${eDay} ${eMonth} ${sYear}`;
  }
  // Different years — build in UTC too, so a year-boundary window (e.g. 31 Dec
  // 00:00Z – 1 Jan 00:00Z) doesn't drift a day on negative-offset clients.
  return `${sDay} ${sMonth} ${sYear} – ${eDay} ${eMonth} ${eYear}`;
};

/** True when two dates fall on the same calendar day in UTC. */
const sameUtcDay = (a: Date, b: Date): boolean =>
  a.getUTCFullYear() === b.getUTCFullYear() &&
  a.getUTCMonth() === b.getUTCMonth() &&
  a.getUTCDate() === b.getUTCDate();

export interface QuizrunGroup {
  quizrunId: number;
  quizrunNumber: number;
  quizrunTitle?: string | null;
  quizrunKind?: string;
  quizrunLanguage: string;
  quizrunDateStart: string;
  quizrunDateFinish: string;
  onsiteEvents: RegistrationEvent[];
  virtualEvents: RegistrationEvent[];
}

/**
 * Date span of a quizrun group. Uses the Quizrun entity's own start/finish
 * (the canonical edition dates shown in the page title) rather than the
 * min/max of its city events — those can drift (e.g. a single late city makes
 * the range look wrong). Falls back to quizrunDateStart if finish is missing.
 */
export const getQuizrunRange = (
  group: QuizrunGroup,
): { start: string; end?: string } => {
  return {
    start: group.quizrunDateStart,
    end: group.quizrunDateFinish || undefined,
  };
};

/** Groups registration events by quizrun ID into QuizrunGroup objects. */
export const groupByQuizrun = (events: RegistrationEvent[]): QuizrunGroup[] => {
  const map = new Map<number, QuizrunGroup>();
  for (const evt of events) {
    let group = map.get(evt.quizrunId);
    if (!group) {
      group = {
        quizrunId: evt.quizrunId,
        quizrunNumber: evt.quizrunNumber,
        quizrunTitle: evt.quizrunTitle,
        quizrunKind: evt.quizrunKind,
        quizrunLanguage: evt.quizrunLanguage,
        quizrunDateStart: evt.quizrunDateStart,
        quizrunDateFinish: evt.quizrunDateFinish,
        onsiteEvents: [],
        virtualEvents: [],
      };
      map.set(evt.quizrunId, group);
    }
    if (evt.isVirtual) {
      group.virtualEvents.push(evt);
    } else {
      group.onsiteEvents.push(evt);
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => new Date(a.quizrunDateStart).getTime() - new Date(b.quizrunDateStart).getTime()
  );
};

/** Finds all upcoming quizruns (date_finish >= now), sorted by date_start */
const findUpcomingQuizruns = (quizruns: Quizrun[]): Quizrun[] => {
  const now = new Date();
  return quizruns
    .filter((qr) => new Date(qr.date_finish) >= now)
    .sort((a, b) => new Date(a.date_start).getTime() - new Date(b.date_start).getTime());
};

/** Collects registration events from all upcoming quizruns, sorted by date with virtual last */
export const collectRegistrationEvents = (quizruns: Quizrun[]): RegistrationEvent[] => {
  const upcoming = findUpcomingQuizruns(quizruns);
  const allEvents = upcoming.flatMap((qr) =>
    eventsToRegistrationFormat(qr.id, qr.number, qr.language, qr.date_start, qr.date_finish, qr.events, qr.title, qr.kind)
  );
  // Sort by date, non-virtual first
  return allEvents.sort((a, b) => {
    if (a.isVirtual !== b.isVirtual) return a.isVirtual ? 1 : -1;
    return new Date(a.date_start).getTime() - new Date(b.date_start).getTime();
  });
};

/**
 * Converts quizrun events to registration event format.
 * Includes all events that haven't finished yet.
 * Sorts so that Virtual events appear last.
 */
export const eventsToRegistrationFormat = (
  quizrunId: number,
  quizrunNumber: number,
  quizrunLanguage: string,
  quizrunDateStart: string,
  quizrunDateFinish: string,
  events: Event[],
  quizrunTitle?: string | null,
  quizrunKind?: string,
): RegistrationEvent[] => {
  const now = new Date();

  const filteredEvents = events
    .filter((evt) => {
      const eventEndDate = evt.date_finish
        ? new Date(evt.date_finish)
        : new Date(evt.date_start);
      return eventEndDate >= now;
    })
    .map((evt) => {
      const isVirtual = isVirtualLocationId(evt.location_id);
      const location = isVirtual
        ? (evt.location?.location_name || "Virtual")
        : [evt.location?.location_name, evt.location?.country].filter(Boolean).join(", ");
      return {
        id: `${quizrunId}-${evt.id}`,
        eventId: evt.id,
        quizrunId,
        quizrunNumber,
        quizrunTitle,
        quizrunKind,
        quizrunLanguage,
        quizrunDateStart,
        quizrunDateFinish,
        name: evt.location?.city || "TBD",
        date_start: evt.date_start,
        date_end: evt.date_finish,
        location,
        locationName: evt.location?.location_name || undefined,
        locationLink: evt.location?.location_link || undefined,
        routeLink: evt.location?.route_link || undefined,
        country: isVirtual ? null : evt.location?.country || null,
        isVirtual,
        priceCents: evt.price_cents ?? null,
        priceCurrency: evt.price_currency ?? null,
        timezone: evt.location?.timezone,
      };
    });

  // Sort: non-virtual events by date, then virtual events last
  return filteredEvents.sort((a, b) => {
    if (a.isVirtual && !b.isVirtual) return 1;
    if (!a.isVirtual && b.isVirtual) return -1;
    return new Date(a.date_start).getTime() - new Date(b.date_start).getTime();
  });
};

/**
 * Split registration events by country for the landing's geo filter. When a
 * country is active (from ?c=<ISO>), `inCountry` holds that country's onsite
 * events plus every virtual event (virtual is location-agnostic — always
 * relevant); `other` holds the remaining onsite events. No active country →
 * everything is in-country and `other` is empty (current behaviour).
 * Matching is case-insensitive on the country display name.
 */
export const partitionEventsByCountry = (
  events: RegistrationEvent[],
  country: string | null,
): { inCountry: RegistrationEvent[]; other: RegistrationEvent[] } => {
  if (!country) return { inCountry: events, other: [] };
  const target = country.toLowerCase();
  const inCountry: RegistrationEvent[] = [];
  const other: RegistrationEvent[] = [];
  for (const evt of events) {
    if (evt.isVirtual || (evt.country?.toLowerCase() === target)) {
      inCountry.push(evt);
    } else {
      other.push(evt);
    }
  }
  return { inCountry, other };
};
